<template>
  <div class="product-image-field" :style="themeStyle">
    <div class="image-source-tabs" role="radiogroup" :aria-label="`${usageLabel}来源`">
      <button
        type="button"
        class="image-source-tab"
        :class="{ 'image-source-tab--active': sourceMode === 'upload' }"
        role="radio"
        :aria-checked="sourceMode === 'upload'"
        @click="changeSourceMode('upload')"
      >
        <i class="el-icon-upload2" />
        直接上传
      </button>
      <button
        type="button"
        class="image-source-tab"
        :class="{ 'image-source-tab--active': sourceMode === 'link' }"
        role="radio"
        :aria-checked="sourceMode === 'link'"
        @click="changeSourceMode('link')"
      >
        <i class="el-icon-link" />
        使用链接
      </button>
    </div>

    <div v-if="sourceMode === 'upload'" class="image-upload-row">
      <el-upload
        ref="uploader"
        action=""
        accept="image/jpeg,image/png,image/webp,image/gif"
        :show-file-list="false"
        :before-upload="beforeUpload"
        :http-request="uploadImage"
      >
        <el-button :loading="uploading" icon="el-icon-picture-outline" plain>
          {{ value ? '重新选择图片' : '选择图片并上传' }}
        </el-button>
      </el-upload>
      <span class="image-field-help">支持 JPG、PNG、WebP、GIF，单张不超过 5MB</span>
    </div>

    <div v-else class="image-link-row">
      <el-input
        :value="value"
        clearable
        maxlength="1024"
        placeholder="粘贴 HTTPS 图片链接"
        @input="inputLink"
      >
        <template slot="prepend">URL</template>
      </el-input>
      <span class="image-field-help">建议使用稳定的 HTTPS/CDN 地址，保存前可在下方检查预览</span>
    </div>

    <div v-if="value" class="image-preview-card">
      <el-image
        class="image-preview"
        :src="previewSource"
        :fit="previewFit"
        :preview-src-list="previewSource ? [previewSource] : []"
        @error="previewError = true"
        @load="previewError = false"
      >
        <div slot="error" class="image-preview__error">
          <i class="el-icon-picture-outline" />
          <span>图片暂时无法预览</span>
        </div>
      </el-image>
      <div class="image-preview-copy">
        <div class="image-preview-head">
          <span class="image-source-badge">{{ sourceMode === 'upload' ? '已上传' : '图片链接' }}</span>
          <span v-if="previewError" class="image-preview-warning">请检查图片地址是否可访问</span>
          <span v-else class="image-preview-success"><i class="el-icon-circle-check" /> 已设置{{ usageLabel }}</span>
        </div>
        <div class="image-preview-url" :title="value">{{ value }}</div>
        <el-button type="text" icon="el-icon-delete" @click="clearImage">移除图片</el-button>
      </div>
    </div>
  </div>
</template>

<script>
import { uploadFile } from '@/api/staff/addStaffApi'
import { resolveImageUrl } from '@/utils/image-url'

const THEMES = {
  JD: { accent: '#c81623', ink: '#9f111c', soft: '#fff1f0', text: '#ffffff' },
  MEITUAN: { accent: '#f6c400', ink: '#6b5400', soft: '#fff8d8', text: '#2f2700' },
  TAOBAO: { accent: '#ff6200', ink: '#c74600', soft: '#fff2e8', text: '#ffffff' },
  TAOBAO_FLASH: { accent: '#c84300', ink: '#a93500', soft: '#fff1e8', text: '#ffffff' },
  JINGLING: { accent: '#ee2932', ink: '#b4232b', soft: '#fff1f2', text: '#ffffff' }
}

export default {
  name: 'ProductImageField',
  props: {
    value: {
      type: String,
      default: ''
    },
    platform: {
      type: String,
      default: 'MEITUAN'
    },
    usageLabel: {
      type: String,
      default: '商品主图'
    },
    previewFit: {
      type: String,
      default: 'cover'
    },
    localUpload: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      sourceMode: this.detectSourceMode(this.value),
      uploading: false,
      previewError: false,
      localPreview: '',
      lastUploadedValue: ''
    }
  },
  computed: {
    previewSource() {
      return this.localPreview || resolveImageUrl(this.value)
    },
    themeStyle() {
      const theme = THEMES[this.platform] || THEMES.MEITUAN
      return {
        '--image-accent': theme.accent,
        '--image-accent-ink': theme.ink,
        '--image-accent-soft': theme.soft,
        '--image-accent-text': theme.text
      }
    }
  },
  watch: {
    value(nextValue) {
      this.previewError = false
      if (nextValue !== this.lastUploadedValue) this.localPreview = ''
    },
    platform() {
      this.sourceMode = this.detectSourceMode(this.value)
    }
  },
  beforeDestroy() {
    this.notifyUploading(false)
  },
  methods: {
    detectSourceMode(value) {
      return /^(https?:)?\/\//i.test(String(value || '').trim()) ? 'link' : 'upload'
    },
    changeSourceMode(mode) {
      this.sourceMode = mode
      this.previewError = false
      this.localPreview = ''
    },
    inputLink(value) {
      this.localPreview = ''
      this.previewError = false
      this.$emit('input', String(value || '').trim())
      this.$emit('change', String(value || '').trim())
    },
    beforeUpload(file) {
      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif']
      const extensionAllowed = /\.(jpe?g|png|webp|gif)$/i.test(file.name || '')
      if (!allowedTypes.includes(file.type) && !extensionAllowed) {
        this.$message.error('请选择 JPG、PNG、WebP 或 GIF 图片')
        return false
      }
      if (file.size > 5 * 1024 * 1024) {
        this.$message.error(`${this.usageLabel}不能超过 5MB`)
        return false
      }
      return true
    },
    async uploadImage({ file }) {
      this.notifyUploading(true)
      this.previewError = false
      this.createLocalPreview(file)
      try {
        const formData = new FormData()
        formData.append('uploadFile', file)
        formData.append('communityId', '-1')
        const response = await uploadFile(formData)
        if (response && response.code != null && Number(response.code) !== 0) {
          throw new Error(response.msg || '上传失败')
        }
        const fileId = response && (
          response.fileId || response.data && response.data.fileId
        )
        const imageUrl = this.localUpload
          ? this.localUploadUrl(fileId)
          : response && (response.url || response.data && response.data.url)
        if (!String(imageUrl || '').trim()) {
          throw new Error('上传成功但接口未返回有效图片地址')
        }
        this.lastUploadedValue = String(imageUrl).trim()
        this.$emit('input', this.lastUploadedValue)
        this.$emit('change', this.lastUploadedValue)
        this.$emit('uploaded', this.lastUploadedValue)
        this.$message.success('商品图片上传成功')
      } catch (error) {
        this.localPreview = ''
        this.$message.error(error.message || '商品图片上传失败')
      } finally {
        this.notifyUploading(false)
        this.$refs.uploader && this.$refs.uploader.clearFiles()
      }
    },
    localUploadUrl(fileId) {
      const value = String(fileId || '').trim().replace(/^\/+/, '')
      const parts = value.split('/')
      if (!/^img\/[A-Za-z0-9._/-]+$/.test(value) || parts.includes('..') || value.includes('//')) {
        throw new Error('上传成功但接口未返回有效的本地文件标识')
      }
      return `/callComponent/download/getFile/file?communityId=-1&fileId=${value}`
    },
    createLocalPreview(file) {
      const reader = new FileReader()
      reader.onload = event => {
        this.localPreview = event.target && event.target.result ? event.target.result : ''
      }
      reader.readAsDataURL(file)
    },
    clearImage() {
      this.localPreview = ''
      this.lastUploadedValue = ''
      this.previewError = false
      this.$emit('input', '')
      this.$emit('change', '')
    },
    notifyUploading(uploading) {
      if (this.uploading === uploading) return
      this.uploading = uploading
      this.$emit('uploading-change', uploading)
    }
  }
}
</script>

<style lang="scss" scoped>
.product-image-field {
  width: 100%;
}

.image-source-tabs {
  display: inline-flex;
  gap: 4px;
  padding: 4px;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  background: #f5f7fa;
}

.image-source-tab {
  min-width: 112px;
  min-height: 40px;
  padding: 0 16px;
  border: 0;
  border-radius: 6px;
  outline: none;
  background: transparent;
  color: #606266;
  cursor: pointer;
  font: inherit;
  transition: background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.image-source-tab i {
  margin-right: 6px;
}

.image-source-tab:hover,
.image-source-tab:focus-visible {
  color: var(--image-accent-ink);
}

.image-source-tab:focus-visible {
  box-shadow: 0 0 0 2px var(--image-accent-soft), 0 0 0 3px var(--image-accent-ink);
}

.image-source-tab--active {
  background: var(--image-accent);
  box-shadow: 0 2px 6px rgba(31, 35, 41, 0.1);
  color: var(--image-accent-text);
  font-weight: 600;
}

.image-source-tab--active:hover {
  color: var(--image-accent-text);
}

.image-upload-row,
.image-link-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 12px;
}

.image-link-row {
  align-items: flex-start;
  flex-direction: column;
}

.image-link-row .el-input {
  width: 100%;
}

.image-field-help {
  color: #909399;
  font-size: 12px;
  line-height: 18px;
}

.image-preview-card {
  display: flex;
  min-height: 112px;
  gap: 14px;
  margin-top: 14px;
  padding: 12px;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  background: #ffffff;
}

.image-preview {
  width: 108px;
  height: 108px;
  flex: none;
  border-radius: 6px;
  background: #f5f7fa;
}

.image-preview__error {
  display: flex;
  width: 108px;
  height: 108px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 6px;
  color: #909399;
  font-size: 12px;
}

.image-preview__error i {
  font-size: 26px;
}

.image-preview-copy {
  display: flex;
  min-width: 0;
  flex: 1;
  align-items: flex-start;
  flex-direction: column;
}

.image-preview-head {
  display: flex;
  min-height: 28px;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.image-source-badge {
  padding: 3px 9px;
  border-radius: 999px;
  background: var(--image-accent-soft);
  color: var(--image-accent-ink);
  font-size: 12px;
  font-weight: 600;
}

.image-preview-success {
  color: #459b5a;
  font-size: 12px;
}

.image-preview-warning {
  color: #c45656;
  font-size: 12px;
}

.image-preview-url {
  width: 100%;
  margin-top: 7px;
  overflow: hidden;
  color: #7b8494;
  font-size: 12px;
  line-height: 18px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.image-preview-copy .el-button {
  margin-top: auto;
  padding: 8px 0 2px;
  color: #c45656;
}
</style>
